import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import {
  getTokenValue,
  styled,
  View,
  isWeb,
  useThemeName,
  type Token,
  type ViewProps,
} from 'tamagui'

import { Image } from '../Image'
import { Icon } from '../Icon'

import type { GuildPrimaryPhotoNew_guild$key } from './__generated__/GuildPrimaryPhotoNew_guild.graphql'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isLargeSizeToken = (size: unknown, tokens: unknown): boolean => {
  if (typeof size !== 'string' || !isRecord(tokens)) return false

  const sizeTokens = tokens.size
  if (!isRecord(sizeTokens)) return false

  const sizeToken = sizeTokens[size]
  if (!isRecord(sizeToken)) return false

  return typeof sizeToken.val === 'number' && sizeToken.val > 40
}

const getTokenSizeValue = (size: Token): number => {
  const value: unknown = getTokenValue(size, 'size')
  if (typeof value === 'number') return value
  if (typeof value === 'string') return Number.parseFloat(value) || 0
  return 0
}

const getPaletteBackgroundType = (
  colorPalette: unknown
): string | undefined => {
  if (!isRecord(colorPalette)) return undefined

  const background = colorPalette.background
  if (!isRecord(background)) return undefined

  return typeof background.type === 'string' ? background.type : undefined
}

const GuildDefaultFrame = styled(View, {
  name: 'GuildDefaultPhoto',
  backgroundColor: '$background',

  borderWidth: 1,
  borderColor: '$borderColor',

  variants: {
    size: {
      '...size': (size, { tokens }) => {
        if (isLargeSizeToken(size, tokens)) {
          return {
            boxShadow: '0 0 0 3px var(--background)',
            borderRadius: '$4',
          }
        }

        return {
          boxShadow: '0 0 0 1px var(--background)',
          borderRadius: '$2',
        }
      },
    },
  } as const,

  defaultVariants: {
    size: '$5',
  },
} as const)

const GuildPrimaryPhotoFrame = styled(Image, {
  name: 'GuildPrimaryPhoto',
  backgroundColor: '$background',

  borderWidth: 1,
  borderColor: '$borderColor',

  variants: {
    size: {
      '...size': (size, { tokens }) => {
        if (isLargeSizeToken(size, tokens)) {
          return {
            boxShadow: '0 0 0 3px var(--background)',
            borderRadius: '$4',
          }
        }

        return {
          boxShadow: '0 0 0 1px var(--background)',
          borderRadius: '$2',
        }
      },
    },
  } as const,

  defaultVariants: {
    size: '$5',
  },
} as const)

type GuildPrimaryPhotoFrameProps = React.ComponentProps<
  typeof GuildPrimaryPhotoFrame
>

export const GuildPrimaryPhoto = GuildPrimaryPhotoFrame.styleable<
  HTMLImageElement,
  {
    guild: GuildPrimaryPhotoNew_guild$key
    size: Token
  } & ViewProps
>(({ guild, size = '$5', ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment GuildPrimaryPhotoNew_guild on Guild
      @argumentDefinitions(
        imageSize: { type: "Int", defaultValue: 100 }
        dpr: { type: "Int", defaultValue: 3 }
      ) {
        name
        rowId

        primaryPhoto {
          transformUrl(
            transform: { w: $imageSize, h: $imageSize, dpr: $dpr, fit: "cover" }
          )
          colorPalette
        }
      }
    `,
    guild
  )

  const parsedSize = useMemo(() => {
    return getTokenSizeValue(size)
  }, [size])

  const themeName = useThemeName()
  const isDark = themeName.startsWith('dark')

  // Use the image's own color palette to detect background type and apply
  // a contrasting background so logos stay visible in both light and dark mode.
  const adaptedBackground = useMemo(() => {
    if (!isWeb || !data.primaryPhoto?.colorPalette) return undefined
    const bgType = getPaletteBackgroundType(data.primaryPhoto.colorPalette)
    if (bgType === 'white' || bgType === 'transparent') {
      return isDark ? 'rgba(255, 255, 255, 0.9)' : undefined
    }
    if (bgType === 'black') {
      return !isDark ? 'rgba(0, 0, 0, 0.9)' : undefined
    }
    return undefined
  }, [isDark, data.primaryPhoto?.colorPalette])

  if (!data.primaryPhoto?.transformUrl) {
    return (
      <GuildDefaultFrame
        {...props}
        ref={ref}
        size={size}
        width={parsedSize}
        height={parsedSize}
        alignItems='center'
        justifyContent='center'
      >
        <Icon
          name='home-filled'
          width={parsedSize / 2}
          height={parsedSize / 2}
        />
      </GuildDefaultFrame>
    )
  }

  const imageProps = props as unknown as GuildPrimaryPhotoFrameProps

  return (
    <GuildPrimaryPhotoFrame
      {...imageProps}
      alt={`Primary Photo for ${data.name}`}
      ref={ref}
      size={size}
      src={data.primaryPhoto.transformUrl}
      width={parsedSize}
      height={parsedSize}
      {...(adaptedBackground ? { backgroundColor: adaptedBackground } : {})}
    />
  )
})
